import api from '@/services/api'

export interface DocumentRepositoryItem {
  document_id: number
  title: string
  source_filename: string | null
  mime_type: string | null
  status: string
  created_at: string
  updated_at: string | null
  current_version_id: number | null
  current_version_number: number | null
  current_version_sha256: string | null
  current_version_file_size_bytes: number | null
}

export interface DocumentRepositoryUploadResponse {
  operation: string
  document_id: number
  version_id: number
  storage_document_id: number
  title: string
  source_filename: string
  mime_type: string | null
  file_size_bytes: number | null
  sha256_hash: string
  version_number: number
  status: string
  created_at: string
}

export interface DocumentRepositoryExtractionTaskQueuedResponse {
  task_id: string
  status: string
  progress_percent: number
  message: string
}

export interface DocumentRepositoryExtractionTaskItem {
  task_id: string
  status: string
  progress_percent: number
  message: string | null
  error_message: string | null
  tenant_id: string | null
  sub_tenant_id: string | null
  user_id: number | null
  document_id: number | null
  document_version_id: number | null
  extraction_id: number | null
  adapter_name: string | null
  pages_count: number | null
  tables_count: number | null
  entities_count: number | null
  created_at: string
  updated_at: string | null
}

export interface RepositoryFinancialAnalysisResponse {
  repository_document_id: number
  repository_version_id: number
  storage_document_id: number
  framework: string
  analysis_source: string
  analysis_run_id: number
}

export interface RepositoryChatContextResponse {
  repository_document_id: number
  repository_version_id: number
  document_id: number
  filename: string
  period_label: string
  title: string
}

export interface DocumentRepositoryVersionItem {
  version_id: number
  version_number: number
  is_current: boolean
  sha256_hash: string
  file_size_bytes: number | null
  storage_key: string | null
  notes: string | null
  uploaded_by: number | null
  created_at: string
}

export interface DocumentRepositoryExtractionItem {
  extraction_id: number
  version_id: number
  adapter_name: string | null
  status: string
  error_message: string | null
  created_by: number | null
  pages_count: number
  tables_count: number
  entities_count: number
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface DocumentRepositoryAuditItem {
  audit_id: number
  action: string
  user_id: number | null
  document_id: number | null
  version_id: number | null
  extraction_id: number | null
  details: Record<string, unknown> | null
  created_at: string
}

export interface DocumentRepositoryDetailResponse {
  document_id: number
  title: string
  source_filename: string | null
  mime_type: string | null
  status: string
  sub_tenant_id: string | null
  uploaded_by: number | null
  created_at: string
  updated_at: string | null
  versions: DocumentRepositoryVersionItem[]
  extractions: DocumentRepositoryExtractionItem[]
  audit_events: DocumentRepositoryAuditItem[]
}

export async function listRepositoryDocuments(params?: {
  tenantId?: string | null
  subTenantId?: string | null
  search?: string | null
  limit?: number
}): Promise<DocumentRepositoryItem[]> {
  const query: Record<string, string | number> = {}
  if (params?.tenantId) query.tenant_id = params.tenantId
  if (params?.subTenantId) query.sub_tenant_id = params.subTenantId
  if (params?.search) query.q = params.search
  if (params?.limit != null) query.limit = params.limit
  const { data } = await api.get<DocumentRepositoryItem[]>('/document-repository', { params: query })
  return data
}

export async function getRepositoryDocumentDetail(
  documentId: number,
  tenantId?: string | null,
): Promise<DocumentRepositoryDetailResponse> {
  const query: Record<string, string> = {}
  if (tenantId) query.tenant_id = tenantId
  const { data } = await api.get<DocumentRepositoryDetailResponse>(
    `/document-repository/${documentId}`,
    { params: query },
  )
  return data
}

export async function uploadRepositoryDocument(params: {
  file: File
  title?: string
  documentId?: number | null
  tenantId?: string | null
  subTenantId?: string | null
  notes?: string
}): Promise<DocumentRepositoryUploadResponse> {
  const form = new FormData()
  form.append('file', params.file)
  if (params.title) form.append('title', params.title)
  if (params.documentId != null) form.append('document_id', String(params.documentId))
  if (params.tenantId) form.append('tenant_id', params.tenantId)
  if (params.subTenantId) form.append('sub_tenant_id', params.subTenantId)
  if (params.notes) form.append('notes', params.notes)

  const { data } = await api.post<DocumentRepositoryUploadResponse>(
    '/document-repository/upload',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

export async function extractRepositoryDocument(params: {
  documentId: number
  tenantId?: string | null
  versionId?: number | null
  adapterName?: string | null
}): Promise<DocumentRepositoryExtractionTaskQueuedResponse> {
  const query: Record<string, string> = {}
  if (params.tenantId) query.tenant_id = params.tenantId
  if (params.versionId != null) query.version_id = String(params.versionId)
  if (params.adapterName) query.adapter_name = params.adapterName

  const { data } = await api.post<DocumentRepositoryExtractionTaskQueuedResponse>(
    `/document-repository/${params.documentId}/extract`,
    null,
    { params: query },
  )
  return data
}

export async function listRepositoryExtractionTasks(params?: {
  tenantId?: string | null
  includeCompleted?: boolean
  limit?: number
}): Promise<DocumentRepositoryExtractionTaskItem[]> {
  const query: Record<string, string | number | boolean> = {}
  if (params?.tenantId) query.tenant_id = params.tenantId
  if (params?.includeCompleted != null) query.include_completed = params.includeCompleted
  if (params?.limit != null) query.limit = params.limit
  const { data } = await api.get<DocumentRepositoryExtractionTaskItem[]>(
    '/document-repository/extraction-tasks',
    { params: query },
  )
  return data
}

export async function analyzeRepositoryDocument(params: {
  documentId: number
  tenantId?: string | null
  versionId?: number | null
  framework?: string
}): Promise<RepositoryFinancialAnalysisResponse> {
  const query: Record<string, string> = {}
  if (params.tenantId) query.tenant_id = params.tenantId
  if (params.versionId != null) query.version_id = String(params.versionId)
  if (params.framework) query.framework = params.framework
  const { data } = await api.post<RepositoryFinancialAnalysisResponse>(
    `/financial/analyze-repository/${params.documentId}`,
    null,
    { params: query },
  )
  return data
}

export async function resolveRepositoryDocumentChatContext(params: {
  documentId: number
  tenantId?: string | null
  versionId?: number | null
}): Promise<RepositoryChatContextResponse> {
  const query: Record<string, string> = {}
  if (params.tenantId) query.tenant_id = params.tenantId
  if (params.versionId != null) query.version_id = String(params.versionId)
  const { data } = await api.post<RepositoryChatContextResponse>(
    `/financial/chat/context/repository/${params.documentId}`,
    null,
    { params: query },
  )
  return data
}
